import styled from 'styled-components';
import { GlassCard } from '@components/primitives/glass-card';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { colors } from '@config/colors';

export const Container = styled.div`
  padding: ${spacing['4xl']} ${spacing.xl};
  max-width: 900px;
  margin: 0 auto;
`;

export const Card = styled(GlassCard)`
  background: ${colors.white};
  padding: ${spacing['4xl']};
  gap: ${spacing.xl};

  @media (max-width: 768px) {
    padding: ${spacing['2xl']};
  }
`;

export const Title = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['4xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.md} 0;
  text-align: center;
`;

export const LastUpdated = styled.p`
  text-align: center;
  color: ${colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  margin: 0 0 ${spacing['2xl']} 0;
`;

export const Section = styled.section`
  margin-bottom: ${spacing['2xl']};
`;

export const SectionTitle = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: ${spacing.xl} 0 ${spacing.md} 0;
`;

export const Paragraph = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.text};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: ${spacing.md} 0;
`;

export const OrderedList = styled.ol`
  margin: ${spacing.md} 0;
  padding-left: ${spacing.xl};

  li {
    font-family: ${typography.fontFamily.body};
    font-size: ${typography.fontSize.base};
    color: ${colors.text};
    line-height: ${typography.lineHeight.bodyRelaxed};
    margin: ${spacing.md} 0;
  }
`;

export const StepNumber = styled.span`
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.highlight.primary};
`;

export const Code = styled.code`
  background: rgba(0, 0, 0, 0.05);
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.sm};
  font-family: monospace;
  font-size: ${typography.fontSize.sm};
`;

export const Link = styled.a`
  color: ${colors.brand.exxtraDark};
  text-decoration: underline;

  &:hover {
    color: ${colors.brand.exxxtraDark};
  }
`;

export const DirectLinkBox = styled.div`
  background: rgba(216, 243, 237, 0.3);
  border: 1px solid ${colors.brand.primary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  margin: ${spacing.xl} 0;
  text-align: center;
`;

export const DirectLinkButton = styled.a`
  display: inline-block;
  background: ${colors.highlight.primary};
  color: ${colors.white};
  padding: ${spacing.md} ${spacing.xl};
  border-radius: ${borderRadius.lg};
  text-decoration: none;
  font-weight: ${typography.fontWeights.medium};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.highlight.dark};
  }
`;

export const ImportantNotice = styled.div`
  background: rgba(255, 200, 0, 0.1);
  border-left: 4px solid ${colors.warning};
  padding: ${spacing.md};
  margin: ${spacing.lg} 0;
  border-radius: ${spacing.sm};
`;

// FAQ Accordion Styles
export const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  margin-top: ${spacing.xl};
`;

export const FAQItem = styled.div`
  border: 1px solid ${colors.glassBorder};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  background: ${colors.overlayWhite40};
`;

export const FAQQuestion = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing.lg};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.overlayWhite60};
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${colors.highlight.primary};
    transition: transform 0.2s ease;
    transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

export const FAQAnswer = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 0.3s ease;
`;

export const FAQAnswerInner = styled.div`
  overflow: hidden;
  padding: 0 ${spacing.lg};

  > *:first-child {
    padding-top: 0;
    margin-top: 0;
  }

  > *:last-child {
    padding-bottom: ${spacing.lg};
    margin-bottom: 0;
  }
`;
